/**
 * API Client utility for making authenticated API calls
 * This can be used when bypassing the proxy or making direct API calls
 */

const API_KEY = process.env.REACT_APP_API_KEY

let accessTokenProvider = null

export const setAccessTokenProvider = (provider) => {
  accessTokenProvider = provider
}

const isJwtShaped = (value) =>
  typeof value === 'string' && value.split('.').length === 3 && value.split('.').every(Boolean)

export const resolveAccessToken = async () => {
  if (process.env.REACT_APP_FEATURE_USER_TOKEN_AUTH !== 'true') return null
  if (!accessTokenProvider) return null

  try {
    const token = await accessTokenProvider()
    return isJwtShaped(token) ? token : null
  } catch (error) {
    console.warn('Failed to resolve access token, falling back to API key:', error)
    return null
  }
}

const appendApiKey = (url) => {
  if (!API_KEY) return url
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}apiKey=${encodeURIComponent(API_KEY)}`
}

/**
 * Makes an authenticated API request
 * @param {string} url - The API endpoint URL
 * @param {object} options - Fetch options
 * @returns {Promise<Response>} - The fetch response
 */
export const authenticatedFetch = async (url, options = {}) => {
  const headers = {
    ...options.headers,
  }

  if (options.body == null) {
    delete headers['Content-Type']
    delete headers['content-type']
  } else if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json'
  }

  const accessToken = await resolveAccessToken()

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`
    return fetch(appendApiKey(url), {
      ...options,
      headers,
    })
  }

  if (API_KEY) {
    headers.Authorization = `Bearer ${API_KEY}`
  }

  return fetch(url, {
    ...options,
    headers,
  })
}

/**
 * Creates an authenticated EventSource connection for Server-Sent Events
 * @param {string} url - The API endpoint URL (without query params)
 * @param {object} params - Query parameters to add to URL
 * @param {object} callbacks - Event callbacks { onProgress, onComplete, onError }
 * @param {string|null} accessToken - Optional Auth0 access token sent as access_token
 * @returns {EventSource} - The EventSource instance (call .close() to disconnect)
 */
export const createAuthenticatedEventSource = (url, params = {}, callbacks = {}, accessToken = null) => {
  const { onProgress, onComplete, onError } = callbacks

  const urlParams = new URLSearchParams(params)
  if (API_KEY) {
    urlParams.set('apiKey', API_KEY)
  }
  if (accessToken) {
    urlParams.set('access_token', accessToken)
  }
  const fullUrl = `${url}?${urlParams.toString()}`

  const eventSource = new EventSource(fullUrl)

  eventSource.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data)

      if (data.type === 'progress' && onProgress) {
        onProgress(data)
      } else if (data.type === 'complete' && onComplete) {
        onComplete(data.data)
        eventSource.close()
      } else if (data.type === 'error' && onError) {
        onError(new Error(data.message || 'Unknown error'))
        eventSource.close()
      }
    } catch (parseError) {
      console.warn('Failed to parse SSE event:', parseError)
    }
  }

  eventSource.onerror = (error) => {
    console.error('EventSource error:', error)
    if (onError) {
      onError(new Error('Connection lost'))
    }
    eventSource.close()
  }

  return eventSource
}

/**
 * Makes an authenticated API request and returns JSON
 * @param {string} url - The API endpoint URL
 * @param {object} options - Fetch options
 * @returns {Promise<object>} - The parsed JSON response
 */
export const authenticatedFetchJson = async (url, options = {}) => {
  const response = await authenticatedFetch(url, options)

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`
    let errorBody = null

    try {
      const responseClone = response.clone()
      errorBody = await responseClone.json()
      if (errorBody && errorBody.message) {
        errorMessage = errorBody.message
      }
    } catch (e) {
      try {
        const responseText = await response.text()
        if (responseText && responseText.trim()) {
          errorMessage = responseText
        }
      } catch (textError) {}
    }

    const error = new Error(errorMessage)
    error.status = response.status
    if (errorBody) error.body = errorBody
    throw error
  }

  return response.json()
}
